import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ReactElement, ReactNode } from 'react';

/**
 * F-111 — Tooltip primitive.
 *
 * Lightweight wrapper around @radix-ui/react-tooltip (already in the
 * project's dependency tree). Use this anywhere we'd otherwise reach
 * for the native `title` attribute, which is unstyleable, inaccessible
 * on touch, and only shows on hover (never on focus).
 *
 * Designed for icon buttons and chip-style labels: pass any focusable
 * trigger element via `asChild`. The trigger keeps its own focus ring
 * and click handler.
 */
interface TooltipProps {
  /** Text rendered inside the tooltip surface. */
  content: ReactNode;
  /** The focusable trigger element (button, anchor). */
  children: ReactElement;
  /** Delay before showing on hover/focus, in ms. */
  delayDuration?: number;
  /**
   * Side preference. Radix will collide-detect and flip when needed.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export default function Tooltip({
  content,
  children,
  delayDuration = 250,
  side = 'top',
}: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} disableHoverableContent>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            className="tooltip-content"
            data-testid="tooltip-content"
          >
            {content}
            <TooltipPrimitive.Arrow className="tooltip-arrow" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
